import type { AudienceSection, B2BAssets } from '../types';
import { B2BCTAButton } from '../components/B2BCTAButton';

export interface B2BAudienceSegmentsProps {
    section: AudienceSection;
    assets: B2BAssets;
}

/**
 * Audience persona rows (Figma `480:26340`, 1920×2271).
 *
 * Absolute-positioned 1920 frame with two alternating rows:
 *  - Row 1 (homeowners): text column left, photo right.
 *    - Heading at x=259.41, y=140, w=573 (28 px Roman, tracking −1.232 px,
 *      leading 1.07).
 *    - Body at x=259, y=237.95, w=523.41 (25 px Roman, tracking −0.75 px,
 *      leading 1.1, color #888).
 *    - "Apply to Join" pill at x=249, y=437.62, w=230.27, h=60 (bg #333,
 *      label 22.7 px 75 Bold, tracking −0.681 px).
 *    - Photo at x=923.88, y=-0.20, w=729.20, h=1043.62.
 *  - Row 2 (specifiers): photo left, text right.
 *    - Photo at x=230.80, y=1227.38, w=729.20, h=1043.62.
 *    - Heading at x=1154, y=1457.65, w=487.62.
 *    - Body at x=1154, y=1527.54, w=539.52.
 *    - "Apply to Join" pill at x=1154, y=1848, w=230.27, h=60.
 *
 * The eyebrow + intro paragraph that used to be inlined here now live in
 * their own `intro-band` section above (Figma `480:29817`, 1920×351).
 *
 * vw conversions assume the 1920 design frame: px / 19.2 = vw.
 */
export const B2BAudienceSegments = ({
    section,
    assets,
}: B2BAudienceSegmentsProps) => {
    const FRAME_H = 2271;

    // Helpers: px → percent of 1920×2271 frame.
    const xPct = (px: number) => `${(px / 1920) * 100}%`;
    const yPct = (px: number) => `${(px / FRAME_H) * 100}%`;
    const wPct = (px: number) => `${(px / 1920) * 100}%`;
    const hPct = (px: number) => `${(px / FRAME_H) * 100}%`;

    // Two row layouts: each row is (heading | body | cta | image) at the
    // Figma-spec coordinates. Indexed by persona slot — 0 = top row
    // (text-left/image-right), 1 = bottom row (image-left/text-right).
    const rows: {
        headingX: number;
        headingY: number;
        headingW: number;
        bodyX: number;
        bodyY: number;
        bodyW: number;
        ctaX: number;
        ctaY: number;
        imageX: number;
        imageY: number;
    }[] = [
        {
            headingX: 259.41,
            headingY: 140,
            headingW: 573,
            bodyX: 259,
            bodyY: 237.95,
            bodyW: 523.41,
            ctaX: 249,
            ctaY: 437.62,
            imageX: 923.88,
            imageY: -0.2,
        },
        {
            headingX: 1154,
            headingY: 1457.65,
            headingW: 487.62,
            bodyX: 1154,
            bodyY: 1527.54,
            bodyW: 539.52,
            ctaX: 1154,
            ctaY: 1848,
            imageX: 230.8,
            imageY: 1227.38,
        },
    ];

    const IMAGE_W = 729.199;
    const IMAGE_H = 1043.624;
    const CTA_W = 230.27;
    const CTA_H = 60;

    return (
        <section
            className="relative w-full bg-white text-[#181818] max-md:!aspect-auto"
            style={{ aspectRatio: `1920 / ${FRAME_H}` }}
            data-b2b-audience
        >
            {/* Desktop: absolute-positioned 1920 frame (Figma 480:26340). */}
            <div className="absolute inset-0 max-md:hidden">
                {section.personas.map((persona, index) => {
                    const row = rows[index];
                    if (!row) return null;
                    const imageSrc = assets[persona.imageAssetKey];

                    return (
                        <article key={persona.name} className="contents">
                            {imageSrc && (
                                <div
                                    className="absolute overflow-hidden"
                                    style={{
                                        left: xPct(row.imageX),
                                        top: yPct(row.imageY),
                                        width: wPct(IMAGE_W),
                                        height: hPct(IMAGE_H),
                                    }}
                                >
                                    <img
                                        src={imageSrc}
                                        alt=""
                                        className="absolute inset-0 size-full object-cover"
                                    />
                                </div>
                            )}
                            <h3
                                className="absolute font-sans text-[1.458vw] font-medium leading-[1.07] tracking-[-0.044em] text-[#181818]"
                                style={{
                                    left: xPct(row.headingX),
                                    top: yPct(row.headingY),
                                    width: wPct(row.headingW),
                                }}
                            >
                                {persona.name}
                            </h3>
                            <p
                                className="absolute font-sans text-[1.302vw] leading-[1.1] tracking-[-0.030em] text-[#888]"
                                style={{
                                    left: xPct(row.bodyX),
                                    top: yPct(row.bodyY),
                                    width: wPct(row.bodyW),
                                }}
                            >
                                {persona.description
                                    .split(/\n\n+/)
                                    .map((para, i, arr) => (
                                        <span
                                            key={i}
                                            className={
                                                i < arr.length - 1
                                                    ? 'mb-[1.042vw] block'
                                                    : 'block'
                                            }
                                        >
                                            {para}
                                        </span>
                                    ))}
                            </p>
                            <div
                                className="absolute"
                                style={{
                                    left: xPct(row.ctaX),
                                    top: yPct(row.ctaY),
                                    width: wPct(CTA_W),
                                    height: hPct(CTA_H),
                                }}
                            >
                                <B2BCTAButton
                                    label={section.ctaLabel}
                                    color="dark"
                                    hideArrow
                                    className="size-full whitespace-nowrap rounded-full bg-[#333] px-[1.436vw] py-[0.591vw] text-[1.183vw] font-bold leading-[1.268] tracking-[-0.030em] text-white shadow-[0_3.243px_11.351px_rgba(0,0,0,0.11)]"
                                />
                            </div>
                        </article>
                    );
                })}
            </div>

            {/* Mobile: stacked flowed layout. Each persona is image-above-text
             *  full-width with the page's 5.4% gutter, matching the rhythm
             *  Phase 2 established for Profile Showcase + IntroBand. The
             *  absolute-positioned Figma mobile spec (521:27956) leaves dead
             *  whitespace on the right of each row, so we override here. */}
            <div className="hidden flex-col gap-[10vw] px-[5.4%] py-[6vw] max-md:flex md:hidden">
                {section.personas.map((persona) => {
                    const imageSrc = assets[persona.imageAssetKey];
                    return (
                        <article
                            key={persona.name}
                            className="flex flex-col gap-[4vw]"
                        >
                            {imageSrc && (
                                <img
                                    src={imageSrc}
                                    alt=""
                                    className="aspect-[729/1043] w-full rounded-[1vw] object-cover"
                                />
                            )}
                            <h3 className="font-sans text-[4.5vw] font-medium leading-[1.1] tracking-[-0.044em] text-[#181818]">
                                {persona.name}
                            </h3>
                            <p className="font-sans text-[3.5vw] leading-[1.3] tracking-[-0.030em] text-[#666]">
                                {persona.description
                                    .split(/\n\n+/)
                                    .map((para, i, arr) => (
                                        <span
                                            key={i}
                                            className={
                                                i < arr.length - 1
                                                    ? 'mb-[3vw] block'
                                                    : 'block'
                                            }
                                        >
                                            {para}
                                        </span>
                                    ))}
                            </p>
                        </article>
                    );
                })}
            </div>
        </section>
    );
};
